"use strict";
import { Pair as FewV2Pair } from "@ring-protocol/few-v2-sdk";
import { InterfacePageName } from "@uniswap/analytics-events";
import { PrefetchBalancesWrapper } from "appGraphql/data/apollo/AdaptiveTokenBalancesProvider";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import { SwapBottomCard } from "components/SwapBottomCard";
import { SwitchLocaleLink } from "components/SwitchLocaleLink";
import { SwapPageWrapper } from "components/swap/styled";
import { useCurrency } from "hooks/Tokens";
import { useAccount } from "hooks/useAccount";
import { useDeferredComponent } from "hooks/useDeferredComponent";
import { useFewV2Pair } from "hooks/useFewV2Pairs";
import { PageType, useIsPage } from "hooks/useIsPage";
import { useStateRehydrated } from "hooks/useStateRehydrated";
import { useRegisterTokenSelectorCurrencies } from "hooks/useTokenSelectorListSearch";
import { PairState } from "hooks/useV2Pairs";
import { SpotModuleSelect } from "pages/Advanced/spot/components/SpotModuleSelect";
import { PATHNAME_BY_MODULE } from "pages/Advanced/spot/constants";
import { SwapSidePanel } from "pages/Swap/SwapSidePanel";
import { parseLockedPairSwapParams, shouldWaitForLockedPairChainMode } from "pages/Swap/lockedPairParams";
import { useResetOverrideOneClickSwapFlag } from "pages/Swap/settings/OneClickSwap";
import { useWebSwapSettings } from "pages/Swap/settings/useWebSwapSettings";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "state/hooks";
import { MultichainContextProvider } from "state/multichain/MultichainContext";
import { useSwapCallback } from "state/sagas/transactions/swapSaga";
import { useWrapCallback } from "state/sagas/transactions/wrapSaga";
import { SwapAndLimitContextProvider } from "state/swap/SwapContext";
import { useInitialCurrencyState } from "state/swap/hooks";
import { TransactionType } from "state/transactions/types";
import { Flex, SegmentedControl, Text, Tooltip, styled } from "ui/src";
import { zIndexes } from "ui/src/theme";
import { useUniswapContext } from "uniswap/src/contexts/UniswapContext";
import { TransactionStatus } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { ProtocolItems } from "uniswap/src/data/tradingApi/__generated__";
import { useIsModeMismatch } from "uniswap/src/features/chains/hooks/useEnabledChains";
import { isTestnetChain } from "uniswap/src/features/chains/utils";
import { RampDirection } from "uniswap/src/features/fiatOnRamp/types";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import { useGetPasskeyAuthStatus } from "uniswap/src/features/passkey/hooks/useGetPasskeyAuthStatus";
import { setIsTestnetModeEnabled } from "uniswap/src/features/settings/slice";
import Trace from "uniswap/src/features/telemetry/Trace";
import { InterfaceEventNameLocal } from "uniswap/src/features/telemetry/constants";
import { sendAnalyticsEvent } from "uniswap/src/features/telemetry/send";
import { TransactionSettingsContextProvider } from "uniswap/src/features/transactions/components/settings/contexts/TransactionSettingsContext";
import { TransactionSettingKey } from "uniswap/src/features/transactions/components/settings/slice";
import { SwapFlow } from "uniswap/src/features/transactions/swap/SwapFlow/SwapFlow";
import { SwapDependenciesContextProvider } from "uniswap/src/features/transactions/swap/contexts/SwapDependenciesContextProvider";
import { SwapFormContextProvider } from "uniswap/src/features/transactions/swap/contexts/SwapFormContext";
import { selectFilteredChainIds } from "uniswap/src/features/transactions/swap/contexts/selectors";
import { useSwapPrefilledState } from "uniswap/src/features/transactions/swap/form/hooks/useSwapPrefilledState";
import { currencyToAsset } from "uniswap/src/features/transactions/swap/utils/asset";
import { CurrencyField } from "uniswap/src/types/currency";
import { SwapTab } from "uniswap/src/types/screens/interface";
import { isMobileWeb } from "utilities/src/platform";
import noop from "utilities/src/react/noop";
import { getParsedChainId } from "utils/chainParams";
import { isIFramed } from "utils/isIFramed";
export default function SwapPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  useFeatureFlag(FeatureFlags.AATestWeb);
  const accountDrawer = useAccountDrawer();
  const { lockedPairParams, maintenanceParams } = useMemo(
    () => parseLockedPairSwapParams(location.search),
    [location.search]
  );
  const requestedChainId = useMemo(() => {
    const chain = new URLSearchParams(location.search).get("chain") ?? void 0;
    return chain ? getParsedChainId({ chain }) : void 0;
  }, [location.search]);
  const isStateRehydrated = useStateRehydrated();
  const isRequestedChainModeMismatch = useIsModeMismatch(requestedChainId);
  const shouldWaitForChainMode = shouldWaitForLockedPairChainMode({
    hasLockedPairParams: Boolean(lockedPairParams),
    hasRequestedChain: Boolean(requestedChainId),
    isStateRehydrated,
    isModeMismatch: isRequestedChainModeMismatch
  });
  useEffect(() => {
    if (isStateRehydrated && lockedPairParams && requestedChainId) {
      dispatch(setIsTestnetModeEnabled(isTestnetChain(requestedChainId)));
    }
  }, [dispatch, isStateRehydrated, lockedPairParams, requestedChainId]);
  const {
    initialInputCurrency,
    initialOutputCurrency,
    initialChainId,
    initialTypedValue,
    initialField,
    triggerConnect
  } = useInitialCurrencyState();
  const lockedInputCurrency = useCurrency(lockedPairParams?.inputCurrency, requestedChainId);
  const lockedOutputCurrency = useCurrency(lockedPairParams?.outputCurrency, requestedChainId);
  const [lockedPairState, lockedPair] = useFewV2Pair(lockedInputCurrency, lockedOutputCurrency, {
    inputsAreFewTokens: true
  });
  const validatedLockedPair = useMemo(() => {
    if (!lockedPairParams || !lockedPair) {
      return void 0;
    }
    return FewV2Pair.getAddress(lockedPair.token0, lockedPair.token1).toLowerCase() === lockedPairParams.targetPair.toLowerCase() ? lockedPair : void 0;
  }, [lockedPair, lockedPairParams]);
  const swapInputCurrency = lockedInputCurrency ?? initialInputCurrency;
  const swapOutputCurrency = lockedOutputCurrency ?? initialOutputCurrency;
  const swapChainId = requestedChainId ?? initialChainId;
  useRegisterTokenSelectorCurrencies(lockedInputCurrency, lockedOutputCurrency);
  useAdjustmentSwapReturn({
    chainId: swapChainId,
    inputAddress: swapInputCurrency?.wrapped.address,
    outputAddress: swapOutputCurrency?.wrapped.address,
    maintenanceParams
  });
  useEffect(() => {
    if (triggerConnect) {
      accountDrawer.open();
      navigate(location.pathname, { replace: true });
    }
  }, [accountDrawer, triggerConnect, navigate, location.pathname]);
  return <Trace logImpression page={InterfacePageName.SWAP_PAGE}><SwapPageWrapper>{lockedPairParams ? <Flex gap="$gap4" px="$spacing12" mb="$spacing16"><Text variant="subheading2">FewV2 locked-pair swap</Text><Text variant="body3" color="$neutral2">
              Route locked to the selected FewV2 pair. Quotes using another pool or multiple hops cannot be submitted.
            </Text><Text variant="body3" color="$statusWarning">
              The selected-pair quote can take several seconds. Wait for a non-zero receive amount before reviewing the
              swap.
            </Text></Flex> : null}{shouldWaitForChainMode ? <Flex px="$spacing12" py="$spacing24"><Text variant="body2" color="$neutral2">
              Loading selected network…
            </Text></Flex> : <Swap
    chainId={swapChainId}
    initialInputCurrency={swapInputCurrency}
    initialOutputCurrency={swapOutputCurrency}
    initialTypedValue={lockedPairParams?.value ?? initialTypedValue}
    initialIndependentField={lockedPairParams?.value ? CurrencyField.INPUT : initialField}
    syncTabToUrl={true}
    usePersistedFilteredChainIds
    requiredPoolAddress={lockedPairParams?.targetPair}
    requiredPoolPair={validatedLockedPair}
    requiredPoolPairIsLoading={Boolean(lockedPairParams && lockedPairState === PairState.LOADING)}
    selectedProtocolsOverride={lockedPairParams ? [ProtocolItems.FEW_V2] : void 0}
    showSidePanel={true}
  />}</SwapPageWrapper>{location.pathname === "/swap" && <SwitchLocaleLink />}</Trace>;
}
function useAdjustmentSwapReturn({
  chainId,
  inputAddress,
  outputAddress,
  maintenanceParams
}) {
  const navigate = useNavigate();
  const transactions = useAppSelector((state) => chainId ? state.localWebTransactions[chainId] : void 0);
  useEffect(() => {
    if (!maintenanceParams || !inputAddress || !outputAddress || !transactions) {
      return;
    }
    const confirmedSwap = Object.values(transactions).find((transaction) => {
      if (transaction.status !== TransactionStatus.Confirmed || transaction.addedTime < maintenanceParams.adjustmentStartedAt || transaction.info.type !== TransactionType.SWAP) {
        return false;
      }
      return transaction.info.inputCurrencyId.toLowerCase().includes(inputAddress.toLowerCase()) && transaction.info.outputCurrencyId.toLowerCase().includes(outputAddress.toLowerCase());
    });
    if (confirmedSwap) {
      const separator = maintenanceParams.returnTo.includes("?") ? "&" : "?";
      navigate(`${maintenanceParams.returnTo}${separator}adjusted=1&txHash=${encodeURIComponent(confirmedSwap.hash)}`, {
        replace: true
      });
    }
  }, [inputAddress, maintenanceParams, navigate, outputAddress, transactions]);
}
export function Swap({
  initialInputCurrency,
  initialOutputCurrency,
  initialTypedValue,
  initialIndependentField,
  chainId,
  hideHeader = false,
  hideFooter = false,
  onCurrencyChange,
  syncTabToUrl,
  swapRedirectCallback,
  tokenColor,
  usePersistedFilteredChainIds = false,
  requiredPoolAddress,
  requiredPoolPair,
  requiredPoolPairIsLoading,
  selectedProtocolsOverride,
  showSidePanel = false
}) {
  const isExplorePage = useIsPage(PageType.EXPLORE);
  const isModeMismatch = useIsModeMismatch(chainId);
  const isSharedSwapDisabled = isModeMismatch && isExplorePage;
  const input = currencyToAsset(initialInputCurrency);
  const output = currencyToAsset(initialOutputCurrency);
  const { isSwapTokenSelectorOpen, swapOutputChainId } = useUniswapContext();
  const persistedFilteredChainIds = useSelector(selectFilteredChainIds);
  const prefilledState = useSwapPrefilledState({
    input,
    output,
    exactAmountToken: initialTypedValue ?? "",
    exactCurrencyField: initialIndependentField ?? CurrencyField.INPUT,
    selectingCurrencyField: isSwapTokenSelectorOpen ? CurrencyField.OUTPUT : void 0,
    selectingCurrencyChainId: swapOutputChainId,
    skipFocusOnCurrencyField: isMobileWeb,
    filteredChainIdsOverride: usePersistedFilteredChainIds ? persistedFilteredChainIds : void 0
  });
  return <MultichainContextProvider initialChainId={chainId}><TransactionSettingsContextProvider
    settingKey={TransactionSettingKey.Swap}
    requiredPoolAddress={requiredPoolAddress}
    requiredPoolPair={requiredPoolPair}
    requiredPoolPairIsLoading={requiredPoolPairIsLoading}
    selectedProtocolsOverride={selectedProtocolsOverride}
  ><SwapAndLimitContextProvider
    initialInputCurrency={initialInputCurrency}
    initialOutputCurrency={initialOutputCurrency}
  ><PrefetchBalancesWrapper><SwapFormContextProvider prefilledState={prefilledState} hideSettings={hideHeader} hideFooter={hideFooter}>{showSidePanel ? <Flex
    row
    width="100%"
    justifyContent="center"
    alignItems="flex-start"
    gap="$spacing40"
    $lg={{ flexDirection: "row", alignItems: "flex-start", gap: "$spacing32" }}
    $md={{ flexDirection: "column", alignItems: "center", gap: "$spacing40" }}
  ><Flex
    width="100%"
    maxWidth={480}
    flexShrink={0}
    position="relative"
    gap="$spacing16"
    opacity={isSharedSwapDisabled ? 0.6 : 1}
  >{isSharedSwapDisabled && <DisabledSwapOverlay />}<UniversalSwapFlow
    hideHeader={hideHeader}
    hideFooter={hideFooter}
    syncTabToUrl={syncTabToUrl}
    swapRedirectCallback={swapRedirectCallback}
    onCurrencyChange={onCurrencyChange}
    prefilledState={prefilledState}
    tokenColor={tokenColor}
  /></Flex><SwapSidePanel /></Flex> : <Flex position="relative" gap="$spacing16" opacity={isSharedSwapDisabled ? 0.6 : 1}>{isSharedSwapDisabled && <DisabledSwapOverlay />}<UniversalSwapFlow
    hideHeader={hideHeader}
    hideFooter={hideFooter}
    syncTabToUrl={syncTabToUrl}
    swapRedirectCallback={swapRedirectCallback}
    onCurrencyChange={onCurrencyChange}
    prefilledState={prefilledState}
    tokenColor={tokenColor}
  /></Flex>}</SwapFormContextProvider></PrefetchBalancesWrapper></SwapAndLimitContextProvider></TransactionSettingsContextProvider></MultichainContextProvider>;
}
const SWAP_TABS = [SwapTab.Swap, SwapTab.Stock, SwapTab.Limit, SwapTab.Send, SwapTab.Buy, SwapTab.Sell];
const TAB_TYPE_TO_LABEL = {
  [SwapTab.Swap]: (t) => t("swap.form.header"),
  [SwapTab.Stock]: (t) => t("swap.stock"),
  [SwapTab.Limit]: () => "Advanced",
  [SwapTab.Send]: (t) => t("send.title"),
  [SwapTab.Buy]: (t) => t("common.buy.label"),
  [SwapTab.Sell]: (t) => t("common.sell.label")
};
const PATHNAME_TO_TAB = {
  "/swap": SwapTab.Swap,
  "/stock": SwapTab.Stock,
  "/send": SwapTab.Send,
  "/limit": SwapTab.Limit,
  "/advanced": SwapTab.Limit,
  "/limit-order": SwapTab.Limit,
  "/buy": SwapTab.Buy,
  "/sell": SwapTab.Sell
};
function UniversalSwapFlow({
  hideHeader = false,
  hideFooter = false,
  disableTokenInputs = false,
  syncTabToUrl = true,
  prefilledState,
  onCurrencyChange,
  swapRedirectCallback,
  tokenColor
}) {
  const [currentTab, setCurrentTab] = useState(SwapTab.Swap);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const swapCallback = useSwapCallback();
  const wrapCallback = useWrapCallback();
  const BuyForm = useDeferredComponent(
    () => import("pages/Swap/Buy/BuyForm").then((module) => ({
      default: module.BuyForm
    }))
  );
  const SendForm = useDeferredComponent(
    () => import("pages/Swap/Send/SendForm").then((module) => ({
      default: module.SendForm
    }))
  );
  useEffect(() => {
    if (pathname === "/send" && isIFramed()) {
      setCurrentTab(SwapTab.Swap);
    } else {
      setCurrentTab(PATHNAME_TO_TAB[pathname] ?? SwapTab.Swap);
    }
  }, [pathname, setCurrentTab]);
  const onTabClick = useCallback(
    (tab) => {
      sendAnalyticsEvent(InterfaceEventNameLocal.SwapTabClicked, { tab });
      if (syncTabToUrl) {
        navigate(tab === SwapTab.Limit ? "/advanced" : `/${tab}`, { replace: true });
      } else {
        setCurrentTab(tab);
      }
    },
    [navigate, syncTabToUrl, setCurrentTab]
  );
  const onAdvancedModuleSelect = useCallback(
    (module) => {
      sendAnalyticsEvent(InterfaceEventNameLocal.SwapTabClicked, { tab: SwapTab.Limit });
      navigate(PATHNAME_BY_MODULE[module], { replace: true });
    },
    [navigate]
  );
  const isFiatOffRampEnabled = useFeatureFlag(FeatureFlags.FiatOffRamp);
  const SWAP_TAB_OPTIONS = useMemo(() => {
    return SWAP_TABS.filter((tab) => {
      if (tab === SwapTab.Sell && !isFiatOffRampEnabled || tab === SwapTab.Send && isFiatOffRampEnabled) {
        return false;
      }
      if (tab === SwapTab.Stock) {
        return true;
      }
      if (tab === SwapTab.Limit) {
        return false;
      }
      if (tab === SwapTab.Buy) {
        return false;
      }
      if (tab === SwapTab.Sell) {
        return false;
      }
      return !(isIFramed() && tab === SwapTab.Send);
    }).map((tab) => ({
      value: tab,
      display: <Text
        variant="buttonLabel3"
        hoverStyle={{ color: "$neutral1" }}
        color={currentTab === tab ? "$neutral1" : "$neutral2"}
        tag="h1"
      >{TAB_TYPE_TO_LABEL[tab](t)}</Text>
    }));
  }, [t, currentTab, isFiatOffRampEnabled]);
  const swapSettings = useWebSwapSettings();
  const resetDisableOneClickSwap = useResetOverrideOneClickSwapFlag();
  const connectorId = useAccount().connector?.id;
  const passkeyAuthStatus = useGetPasskeyAuthStatus(connectorId);
  const isBuyEnabled = useFeatureFlag(FeatureFlags.ShowBuy);
  const isSellEnabled = useFeatureFlag(FeatureFlags.ShowSell);
  return <Flex>{!hideHeader && SWAP_TAB_OPTIONS.length > 1 && <Flex row gap="$spacing8" flexWrap="wrap" alignItems="center"><SegmentedControl
    outlined={false}
    size="large"
    options={SWAP_TAB_OPTIONS}
    selectedOption={currentTab}
    onSelectOption={onTabClick}
    gap={isMobileWeb ? "$spacing8" : void 0}
  />{syncTabToUrl && <SpotModuleSelect onSelectModule={onAdvancedModuleSelect} />}</Flex>}{!hideHeader && SWAP_TAB_OPTIONS.length === 1 && SWAP_TAB_OPTIONS[0] && <Flex row gap="$spacing16" px="$spacing12" height={42} alignItems="center">{SWAP_TAB_OPTIONS[0].display}</Flex>}{(currentTab === SwapTab.Swap || currentTab === SwapTab.Stock) && <Flex gap="$spacing16"><SwapDependenciesContextProvider swapCallback={swapCallback} wrapCallback={wrapCallback}><SwapFlow
    settings={swapSettings}
    hideHeader={hideHeader}
    hideFooter={hideFooter}
    onClose={noop}
    swapRedirectCallback={swapRedirectCallback}
    onCurrencyChange={onCurrencyChange}
    prefilledState={prefilledState}
    tokenColor={tokenColor}
    onSubmitSwap={resetDisableOneClickSwap}
    passkeyAuthStatus={passkeyAuthStatus}
  /></SwapDependenciesContextProvider><SwapBottomCard /></Flex>}{isSellEnabled && currentTab === SwapTab.Send && SendForm && <SendForm disableTokenInputs={disableTokenInputs} onCurrencyChange={onCurrencyChange} />}{isBuyEnabled && currentTab === SwapTab.Buy && BuyForm && <BuyForm
    rampDirection={RampDirection.ONRAMP}
    disabled={disableTokenInputs}
    initialCurrency={prefilledState?.output}
  />}{isSellEnabled && currentTab === SwapTab.Sell && BuyForm && <BuyForm
    rampDirection={RampDirection.OFFRAMP}
    disabled={disableTokenInputs}
    initialCurrency={prefilledState?.output}
  />}</Flex>;
}
const DisabledOverlay = styled(Flex, {
  position: "absolute",
  width: "100%",
  height: "100%",
  zIndex: zIndexes.overlay
});
const DisabledSwapOverlay = () => {
  const { t } = useTranslation();
  return <DisabledOverlay cursor="not-allowed"><Tooltip placement="left-start"><Tooltip.Content animationDirection="left"><Tooltip.Arrow /><Text variant="body4">{t("testnet.unsupported")}</Text></Tooltip.Content><Tooltip.Trigger position="relative" width="100%" height="100%"><DisabledOverlay /></Tooltip.Trigger></Tooltip></DisabledOverlay>;
};
